// Favorites for MLS listings — the behaviour of the server-rendered button
// (webdelo.idx::components.favorite-button) and of the Remove button on the
// "no longer available" placeholder (webdelo.idx::partials.favorite-unavailable).
//
// The server always renders the button inactive: IDX pages are cached, so the
// state can only come from the visitor's own storage. It is painted here, from
// the Vuex favorites module — the single source of truth, shared with the
// own-estate buttons and the header counter.
//
// Clicks are delegated to document because a good part of these buttons arrive
// after load (map list, pinned cards, the /favorites page). Whoever inserts such
// HTML calls window.syncIdxFavorites(root) afterwards to paint it.

const BUTTON = '[data-idx-favorite]';
const REMOVE = '[data-idx-favorite-remove]';
const ACTIVE = '_active';

function paint(button, saved) {
    const on = saved.has(button.getAttribute('data-idx-favorite'));
    const label = button.getAttribute(on ? 'data-label-on' : 'data-label-off');

    button.classList.toggle(ACTIVE, on);
    button.setAttribute('aria-pressed', on ? 'true' : 'false');

    if (label) {
        button.setAttribute('aria-label', label);
    }
}

export function initIdxFavorites(store) {
    const savedKeys = () => new Set(store.getters['favorites/listingKeys'] || []);

    const sync = (root = document) => {
        if (!root || typeof root.querySelectorAll !== 'function') {
            return;
        }

        const saved = savedKeys();

        if (root instanceof Element && root.matches(BUTTON)) {
            paint(root, saved);
        }

        root.querySelectorAll(BUTTON).forEach((button) => paint(button, saved));
    };

    window.syncIdxFavorites = sync;

    document.addEventListener('click', (event) => {
        if (!(event.target instanceof Element)) {
            return;
        }

        const button = event.target.closest(BUTTON);

        if (button) {
            // The card image is a link: a click on the heart must not open the listing.
            event.preventDefault();
            store.dispatch('favorites/toggleListing', button.getAttribute('data-idx-favorite'));

            return;
        }

        const remove = event.target.closest(REMOVE);

        if (remove) {
            event.preventDefault();
            store.dispatch('favorites/removeListing', remove.getAttribute('data-idx-favorite-remove'));

            // The placeholder only ever shows on /favorites; take its cell along.
            const cell = remove.closest('[data-idx-fav-key]') || remove.closest('.catalog-list__col');

            if (cell) {
                cell.remove();
            }
        }
    });

    // Every change — this tab's click, another tab's, "Clear all" — repaints all
    // buttons on the page, so two cards of the same listing never disagree.
    store.watch((state) => state.favorites.favoritesIds.listings, () => sync(document));

    // Other tabs. The event only fires in the tabs that did NOT write, so there
    // is no echo; `key === null` is localStorage.clear().
    window.addEventListener('storage', (event) => {
        if (event.key === 'favorites' || event.key === null) {
            store.dispatch('favorites/sync');
        }
    });

    return sync;
}
